import { matchCustomMediaQuery } from '@tesla/design-system';
import mediaTokens from '@tesla/design-system-tokens/dist/media.js';
import { useEffect, useState } from 'react';
import { useSSR } from './use-ssr.js';
const defaultMediaQueries = {
    isDensity2x: mediaTokens['density--2x'],
    isDensity3x: mediaTokens['density--3x'],
    isDensity4x: mediaTokens['density--4x'],
    isDesktopLargeUp: mediaTokens['desktop-large-up'],
    isDesktopOnly: mediaTokens['desktop-only'],
    isDesktopUp: mediaTokens['desktop-up'],
    isPhoneOnly: mediaTokens['phone-only'],
    isTabletLandscapeOnly: mediaTokens['tablet-landscape-only'],
    isTabletLandscapeUp: mediaTokens['tablet-landscape-up'],
    isTabletPortraitOnly: mediaTokens['tablet-portrait-only'],
    isTabletPortraitUp: mediaTokens['tablet-portrait-up'],
};
/**
 * mediaQueries provides a list of media queries from /tokens/media.json in design-system
 */
export const getMediaQueryLists = () => Object.entries(defaultMediaQueries).reduce((acc, [key, query]) => {
    acc[key] = matchCustomMediaQuery(query);
    return acc;
}, {});
/**
 * getMediaQueryMatches
 * @returns the boolean values for `matches` of each media query
 */
export const getMediaQueryMatches = (queries) => Object.entries(queries).reduce((queries, [query, queryList]) => ({ ...queries, [query]: queryList.matches }), {});
/**
 * useMediaQuery returns matching media queries
 * @returns an object with boolean values for matching media queries
 */
export const useMediaQuery = (customQueries) => {
    // Gets the initial matching media queries to set the state
    const { isBrowser, isServer } = useSSR();
    const [queries] = useState(() => ({
        ...defaultMediaQueries,
        ...(customQueries !== null && customQueries !== void 0 ? customQueries : {}),
    }));
    const [matchingQueries, setMatchingQueries] = useState(() => Object.entries(queries).reduce((acc, [key, query]) => {
        acc[key] = matchCustomMediaQuery(query).matches;
        return acc;
    }, {}));
    useEffect(() => {
        // When a query fires the change listener, it updates the state with matches
        const onChange = (e) => {
            var _a;
            const [key] = (_a = Object.entries(queries).find(([, query]) => query === e.media)) !== null && _a !== void 0 ? _a : [];
            if (key) {
                setMatchingQueries((prev) => ({ ...prev, [key]: e.matches }));
            }
        };
        const matchQueriesList = [];
        // Gets the media queries (not their boolean values) and allows us to bind the change listeners. This also ensures in SSR scenario that matchMedia is run at least once on client.
        setMatchingQueries(Object.entries(queries).reduce((acc, [key, query]) => {
            var _a;
            const _key = key;
            const mediaQuery = matchCustomMediaQuery(query);
            matchQueriesList.push(mediaQuery);
            (_a = mediaQuery.addEventListener) === null || _a === void 0 ? void 0 : _a.call(mediaQuery, 'change', onChange);
            acc[_key] = mediaQuery.matches;
            return acc;
        }, {}));
        return () => {
            matchQueriesList.forEach((query) => {
                var _a;
                (_a = query.removeEventListener) === null || _a === void 0 ? void 0 : _a.call(query, 'change', onChange);
            });
        };
        // Since we ask for an object, `queries`, which should never change, could cause unnecessary frequent re-renders
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return { ...matchingQueries, isBrowser, isServer };
};
